import CommonApi from "../common/CommonApi";
import axios from "axios";
import SuccessResponse from "../dto/common/SuccessResponse";
import StoreListDto from "../dto/store/StoreListDto";
import CommonRequestBodyDto from "../dto/common/CommonRequestBodyDto";
import StoreSaveReqDto from "../dto/store/StoreSaveReqDto";
import StoreSaveRespDto from "../dto/store/StoreSaveRespDto";
import StoreUpdateReqDto from "../dto/store/StoreUpdateReqDto";
import StoreReportDetailDto from "../dto/store/StoreReportDetailDto";
import StoreRecentRevenueDto from "../dto/store/StoreRecentRevenueDto";
import StoreTotalRevenueDto, { StoreRevenuePeriodType } from "../dto/store/StoreTotalRevenueDto";
import StoreRevenueSummaryDto from "../dto/store/StoreRevenueSummaryDto";
import StoreRevenueSummaryByStoreDto from "../dto/store/StoreRevenueSummaryByStoreDto";
import StoreQrCodeDto from "../dto/store/StoreQrCodeDto";
import StoreDetailDto from "../dto/store/StoreDetailDto";
import DeviceMemoListDto from "../dto/store/DeviceMemoListDto";
import DeviceMemoUpdateReqDto from "../dto/store/DeviceMemoUpdateReqDto";
import StoreDeviceDto from "../dto/store/StoreDeviceDto";
import StoreDeviceStatusDto from "../dto/store/StoreDeviceStatusDto";
import StoreDeviceStatusSummaryDto from "../dto/store/StoreDeviceStatusSummaryDto";
import EncryptUtil from "../../../common/utils/EncryptUtil";

class RealStoreApi extends CommonApi {
    async getStoreList({
        page,
        pageSize,
        shopCode,
        shopName,
        phoneNumber,
        address,
        states,
    }: {
        page: number;
        pageSize: number;
        shopCode?: string;
        shopName?: string;
        phoneNumber?: string;
        address?: string;
        states?: string;
    }): Promise<SuccessResponse<StoreListDto>> {
        const baseHeaders = this.makeBaseHeader();

        const query: Record<string, any> = { page, pageSize };
        if (shopCode) query.shopCode = shopCode;
        if (shopName) query.shopName = shopName;
        if (phoneNumber) query.phoneNumber = phoneNumber;
        if (address) query.address = address;
        if (states) query.states = states;

        await axios.options(`${this.BASE_SERVICE_URL}/stores`, {
            headers: baseHeaders,
        });

        const response = await axios.get<SuccessResponse<StoreListDto>>(`${this.BASE_SERVICE_URL}/stores`, {
            headers: baseHeaders,
            params: query,
        });

        return response.data;
    }

    async getStoreDetail({ storeId }: { storeId: string }): Promise<SuccessResponse<StoreDetailDto>> {
        const baseHeaders = this.makeBaseHeader();

        await axios.options(`${this.BASE_SERVICE_URL}/stores/storeid/${storeId}`, {
            headers: baseHeaders,
        });

        const response = await axios.get<SuccessResponse<StoreDetailDto>>(
            `${this.BASE_SERVICE_URL}/stores/storeid/${storeId}`,
            { headers: baseHeaders }
        );

        return response.data;
    }

    async getStoreAdminList({
        page,
        pageSize,
        franchisesCode,
    }: {
        page: number;
        pageSize: number;
        franchisesCode: string;
    }): Promise<SuccessResponse<StoreListDto>> {
        const baseHeaders = this.makeBaseHeader();

        await axios.options(`${this.BASE_SERVICE_URL}/stores/admin`, {
            headers: baseHeaders,
        });

        const response = await axios.get<SuccessResponse<StoreListDto>>(`${this.BASE_SERVICE_URL}/stores/admin`, {
            headers: baseHeaders,
            params: { page, pageSize, franchisesCode },
        });

        return response.data;
    }

    async saveStore(reqDto: CommonRequestBodyDto<StoreSaveReqDto>): Promise<SuccessResponse<StoreSaveRespDto>> {
        let baseHeaders = this.makeBaseHeaderWithClientVersion({ xApiVersion: 3.1 });
        baseHeaders = {
            ...baseHeaders,
            // "X-Api-Version": "3.1",
        };

        await axios.options(`${this.BASE_SERVICE_URL}/stores`, {
            headers: baseHeaders,
        });

        const response = await axios.post<SuccessResponse<StoreSaveRespDto>>(
            `${this.BASE_SERVICE_URL}/stores`,
            reqDto,
            {
                headers: baseHeaders,
            }
        );

        return response.data;
    }

    // TODO: Method 확인 필요 — API 문서에 Post로 잘못 표기됨 (매장삭제와 본문이 copy-paste됨)
    async updateStore(reqDto: CommonRequestBodyDto<StoreUpdateReqDto>): Promise<SuccessResponse<StoreSaveRespDto>> {
        const baseHeaders = this.makeBaseHeader();

        await axios.options(`${this.BASE_SERVICE_URL}/stores`, {
            headers: baseHeaders,
        });

        const response = await axios.put<SuccessResponse<StoreSaveRespDto>>(`${this.BASE_SERVICE_URL}/stores`, reqDto, {
            headers: baseHeaders,
        });

        return response.data;
    }

    // TODO: Method 확인 필요 — API 문서에 Post로 잘못 표기됨
    async deleteStore({ storeId }: { storeId: string }): Promise<SuccessResponse<object>> {
        const baseHeaders = this.makeBaseHeader();

        await axios.options(`${this.BASE_SERVICE_URL}/stores`, {
            headers: baseHeaders,
        });

        const response = await axios.delete<SuccessResponse<object>>(`${this.BASE_SERVICE_URL}/stores`, {
            headers: baseHeaders,
            params: { storeId },
        });

        return response.data;
    }

    async getStoreReportDetail({
        storeId,
        period,
        startDate,
        endDate,
    }: {
        storeId: string;
        period: "Day" | "Week" | "Month" | "Year";
        startDate: string;
        endDate: string;
    }): Promise<SuccessResponse<StoreReportDetailDto>> {
        const baseHeaders = this.makeBaseHeader();

        await axios.options(`${this.BASE_SERVICE_URL}/stores/report-detail`, {
            headers: baseHeaders,
        });

        const response = await axios.get<SuccessResponse<StoreReportDetailDto>>(
            `${this.BASE_SERVICE_URL}/stores/report-detail`,
            {
                headers: baseHeaders,
                params: { storeId, period, startDate, endDate },
            }
        );

        return response.data;
    }

    async getStoreDeviceStatus({ storeId }: { storeId: string }): Promise<SuccessResponse<StoreDeviceStatusDto>> {
        const baseHeaders = this.makeBaseHeader();

        await axios.options(`${this.BASE_SERVICE_URL}/status/${storeId}`, {
            headers: baseHeaders,
        });

        const response = await axios.get<SuccessResponse<StoreDeviceStatusDto>>(
            `${this.BASE_SERVICE_URL}/status/${storeId}`,
            {
                headers: baseHeaders,
            }
        );

        return response.data;
    }

    async getStoreDeviceStatusSummary({
        storeId,
    }: {
        storeId: string;
    }): Promise<SuccessResponse<StoreDeviceStatusSummaryDto>> {
        const baseHeaders = this.makeBaseHeader();

        await axios.options(`${this.BASE_SERVICE_URL}/stores/${storeId}/device-status`, {
            headers: baseHeaders,
        });

        const response = await axios.get<SuccessResponse<StoreDeviceStatusSummaryDto>>(
            `${this.BASE_SERVICE_URL}/stores/${storeId}/device-status`,
            {
                headers: baseHeaders,
            }
        );

        return response.data;
    }

    async getStoreRecentRevenue({
        storeId,
        startDate,
        endDate,
    }: {
        storeId: string;
        startDate: string;
        endDate: string;
    }): Promise<SuccessResponse<StoreRecentRevenueDto[]>> {
        const baseHeaders = this.makeBaseHeader();

        await axios.options(`${this.BASE_SERVICE_URL}/stores/report-recent-revenu`, {
            headers: baseHeaders,
        });

        const response = await axios.get<SuccessResponse<StoreRecentRevenueDto[]>>(
            `${this.BASE_SERVICE_URL}/stores/report-recent-revenu`,
            {
                headers: baseHeaders,
                params: { storeId, startDate, endDate },
            }
        );

        return response.data;
    }

    // GET /franchises/report/revenue?today=%Y-%m-%d
    async getStoreRevenueSummary({ today }: { today: string }): Promise<SuccessResponse<StoreRevenueSummaryDto>> {
        const baseHeaders = this.makeBaseHeader();

        await axios.options(`${this.BASE_SERVICE_URL}/report/revenue`, {
            headers: baseHeaders,
        });

        const response = await axios.get<SuccessResponse<StoreRevenueSummaryDto>>(
            `${this.BASE_SERVICE_URL}/report/revenue`,
            {
                headers: baseHeaders,
                params: { today },
            }
        );

        return response.data;
    }

    // GET /franchises/report/revenue/<store-id>/summary?today=%Y-%m-%d
    async getStoreRevenueSummaryByStore({
        storeId,
        today,
    }: {
        storeId: string;
        today: string;
    }): Promise<SuccessResponse<StoreRevenueSummaryByStoreDto>> {
        const baseHeaders = this.makeBaseHeader();

        await axios.options(`${this.BASE_SERVICE_URL}/report/revenue/${storeId}/summary`, {
            headers: baseHeaders,
        });

        const response = await axios.get<SuccessResponse<StoreRevenueSummaryByStoreDto>>(
            `${this.BASE_SERVICE_URL}/report/revenue/${storeId}/summary`,
            {
                headers: baseHeaders,
                params: { today },
            }
        );

        return response.data;
    }

    // GET /franchises/report/revenue/<store-id>?startDate=%Y-%m-%d&endDate=%Y-%m-%d&periodType=[day|week|month|year|hour|dayofweek]
    async getStoreRevenueByStore({
        storeId,
        periodType,
        startDate,
        endDate,
    }: {
        storeId: string;
        periodType: StoreRevenuePeriodType;
        startDate: string;
        endDate: string;
    }): Promise<SuccessResponse<StoreTotalRevenueDto[]>> {
        const baseHeaders = this.makeBaseHeader();

        await axios.options(`${this.BASE_SERVICE_URL}/report/revenue/${storeId}`, {
            headers: baseHeaders,
        });

        const response = await axios.get<SuccessResponse<StoreTotalRevenueDto[]>>(
            `${this.BASE_SERVICE_URL}/report/revenue/${storeId}`,
            {
                headers: baseHeaders,
                params: { periodType, startDate, endDate },
            }
        );

        return response.data;
    }

    async getStoreQrCode({ storeId }: { storeId: string }): Promise<SuccessResponse<StoreQrCodeDto>> {
        const baseHeaders = this.makeBaseHeader();

        await axios.options(`${this.BASE_SERVICE_URL}/stores/report-qrcode`, {
            headers: baseHeaders,
        });

        const response = await axios.get<SuccessResponse<StoreQrCodeDto>>(
            `${this.BASE_SERVICE_URL}/stores/report-qrcode`,
            {
                headers: baseHeaders,
                params: { storeId },
            }
        );

        return response.data;
    }

    async saveStoreImage({ storeId, image }: { storeId: string; image: File }): Promise<SuccessResponse<object>> {
        const baseHeaders = this.makeMultipartHeader();
        const formData = new FormData();
        formData.append("content", image);

        await axios.options(
            `${this.BASE_SERVICE_URL}/stores/${storeId}/image?alternativeText=${storeId + EncryptUtil.generateSecureRandomString(4)}`,
            {
                headers: baseHeaders,
            }
        );

        const response = await axios.post<SuccessResponse<object>>(
            `${this.BASE_SERVICE_URL}/stores/${storeId}/image?alternativeText=${storeId + EncryptUtil.generateSecureRandomString(4)}`,
            formData,
            {
                headers: baseHeaders,
            }
        );

        return response.data;
    }

    async getStoreImages({ storeId }: { storeId: string }): Promise<SuccessResponse<object>> {
        const baseHeaders = this.makeBaseHeader();

        await axios.options(`${this.BASE_SERVICE_URL}/stores/${storeId}/image`, {
            headers: baseHeaders,
        });

        const response = await axios.get<SuccessResponse<object>>(`${this.BASE_SERVICE_URL}/stores/${storeId}/image`, {
            headers: baseHeaders,
        });

        return response.data;
    }

    async getGpsStoreList({
        radius,
        userLatitude,
        userLongitude,
        pageSize,
    }: {
        radius: string;
        userLatitude: string;
        userLongitude: string;
        pageSize: number;
    }): Promise<SuccessResponse<object>> {
        const baseHeaders = this.makeBaseHeader();

        await axios.options(`${this.BASE_SERVICE_URL}/stores/info/search`, {
            headers: baseHeaders,
        });

        const response = await axios.get<SuccessResponse<StoreQrCodeDto>>(
            `${this.BASE_SERVICE_URL}/stores/info/search`,
            {
                headers: baseHeaders,
                params: {
                    searchType: "gps",
                    radius,
                    latitude: userLatitude,
                    longitude: userLongitude,
                },
            }
        );

        return response.data;
    }

    // /franchises/stores/sync-shop-code
    async syncShopCode(): Promise<SuccessResponse<object>> {
        const baseHeaders = this.makeBaseHeaderWithClientVersion({ xApiVersion: 3.2 });

        await axios.options(`${this.BASE_SERVICE_URL}/stores/sync-shop-code`, {
            headers: baseHeaders,
        });

        const response = await axios.post<SuccessResponse<object>>(
            `${this.BASE_SERVICE_URL}/stores/sync-shop-code`,
            {},
            {
                headers: baseHeaders,
            }
        );

        return response.data;
    }

    // /franchises/stores/<storeId>/device
    async getStoreDevice({ storeId }: { storeId: string }): Promise<SuccessResponse<StoreDeviceDto[]>> {
        const baseHeaders = this.makeBaseHeader();

        await axios.options(`${this.BASE_SERVICE_URL}/stores/${storeId}/device`, {
            headers: baseHeaders,
        });

        const response = await axios.get<SuccessResponse<StoreDeviceDto[]>>(
            `${this.BASE_SERVICE_URL}/stores/${storeId}/device`,
            {
                headers: baseHeaders,
            }
        );

        return response.data;
    }

    // /franchises/devices/<device-id>/memo
    async getDeviceMemo({ deviceId }: { deviceId: string }): Promise<SuccessResponse<DeviceMemoListDto>> {
        const baseHeaders = this.makeBaseHeader();

        await axios.options(`${this.BASE_SERVICE_URL}/devices/${deviceId}/memo`, {
            headers: baseHeaders,
        });

        const response = await axios.get<SuccessResponse<DeviceMemoListDto>>(
            `${this.BASE_SERVICE_URL}/devices/${deviceId}/memo`,
            {
                headers: baseHeaders,
            }
        );

        return response.data;
    }

    // POST /franchises/devices/<device-id>/memo
    async createDeviceMemo({ deviceId, memo }: { deviceId: string; memo: string }): Promise<SuccessResponse<object>> {
        const baseHeaders = this.makeBaseHeader();

        const reqDto: CommonRequestBodyDto<DeviceMemoUpdateReqDto> = { request: { memo } };

        await axios.options(`${this.BASE_SERVICE_URL}/devices/${deviceId}/memo`, {
            headers: baseHeaders,
        });

        const response = await axios.post<SuccessResponse<object>>(
            `${this.BASE_SERVICE_URL}/devices/${deviceId}/memo`,
            reqDto,
            {
                headers: baseHeaders,
            }
        );

        return response.data;
    }

    // PUT /franchises/devices/<device-id>/memo  (x-memo-id 헤더로 대상 메모 지정)
    async updateDeviceMemo({
        deviceId,
        memoId,
        memo,
    }: {
        deviceId: string;
        memoId: number;
        memo: string;
    }): Promise<SuccessResponse<string>> {
        const baseHeaders = {
            ...this.makeBaseHeader(),
            "x-memo-id": String(memoId),
        };

        const reqDto: CommonRequestBodyDto<DeviceMemoUpdateReqDto> = { request: { memo } };

        await axios.options(`${this.BASE_SERVICE_URL}/devices/${deviceId}/memo`, {
            headers: baseHeaders,
        });

        const response = await axios.put<SuccessResponse<string>>(
            `${this.BASE_SERVICE_URL}/devices/${deviceId}/memo`,
            reqDto,
            {
                headers: baseHeaders,
            }
        );

        return response.data;
    }

    // DELETE /franchises/devices/<device-id>/memo  (x-memo-id 헤더로 대상 메모 지정)
    async deleteDeviceMemo({
        deviceId,
        memoId,
    }: {
        deviceId: string;
        memoId: number;
    }): Promise<SuccessResponse<string>> {
        const baseHeaders = {
            ...this.makeBaseHeader(),
            "x-memo-id": String(memoId),
        };

        await axios.options(`${this.BASE_SERVICE_URL}/devices/${deviceId}/memo`, {
            headers: baseHeaders,
        });

        const response = await axios.delete<SuccessResponse<string>>(
            `${this.BASE_SERVICE_URL}/devices/${deviceId}/memo`,
            {
                headers: baseHeaders,
            }
        );

        return response.data;
    }
}

export default RealStoreApi;
